import type { ColorPalette } from '../types';

const FALLBACK_PALETTES: ColorPalette[] = [
  {
    primary: [255, 0, 120],
    secondary: [0, 220, 255],
    accent: [255, 200, 0],
    background: [8, 4, 20],
    glow: [180, 0, 255],
  },
  {
    primary: [0, 255, 140],
    secondary: [255, 80, 0],
    accent: [255, 255, 255],
    background: [4, 12, 8],
    glow: [0, 180, 255],
  },
];

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

function quantizeColor([r, g, b]: [number, number, number]): [number, number, number] {
  const [h, s, l] = rgbToHsl(r, g, b);
  if (s < 12 || l < 8 || l > 92) return [0, 0, 0];
  return hslToRgb(h, Math.min(100, s * 1.15), Math.min(72, Math.max(28, l)));
}

export function paletteFromSeed(seed: number): ColorPalette {
  const idx = seed % FALLBACK_PALETTES.length;
  return FALLBACK_PALETTES[idx];
}

export async function extractPaletteFromImage(
  imageUrl: string,
  seed: number
): Promise<ColorPalette> {
  if (!imageUrl) return paletteFromSeed(seed);

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const size = 48;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(paletteFromSeed(seed));
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        const buckets = new Map<string, { rgb: [number, number, number]; count: number }>();

        for (let i = 0; i < data.length; i += 16) {
          const rgb: [number, number, number] = [data[i], data[i + 1], data[i + 2]];
          const q = quantizeColor(rgb);
          if (q[0] === 0 && q[1] === 0 && q[2] === 0) continue;
          const key = q.join(',');
          const existing = buckets.get(key);
          if (existing) existing.count += 1;
          else buckets.set(key, { rgb: q, count: 1 });
        }

        const sorted = [...buckets.values()].sort((a, b) => b.count - a.count);
        if (sorted.length === 0) {
          resolve(paletteFromSeed(seed));
          return;
        }

        const primary = sorted[0].rgb;
        const secondary = sorted[1]?.rgb ?? hslToRgb((rgbToHsl(...primary)[0] + 120) % 360, 85, 55);
        const accent = sorted[2]?.rgb ?? hslToRgb((rgbToHsl(...primary)[0] + 60) % 360, 90, 62);
        const [h] = rgbToHsl(...primary);

        resolve({
          primary,
          secondary,
          accent,
          background: hslToRgb(h, 45, 6),
          glow: hslToRgb((h + 180) % 360, 90, 58),
        });
      } catch {
        resolve(paletteFromSeed(seed));
      }
    };
    img.onerror = () => resolve(paletteFromSeed(seed));
    img.src = imageUrl;
  });
}

export function paletteToHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export function applyVisualStyle(
  palette: ColorPalette,
  style: import('../types').VisualStyle
): ColorPalette {
  const mix = (a: [number, number, number], b: [number, number, number], t: number) =>
    a.map((v, i) => Math.round(v * (1 - t) + b[i] * t)) as [number, number, number];

  switch (style) {
    case 'minimal':
      return {
        ...palette,
        primary: mix(palette.primary, [220, 220, 230], 0.55),
        secondary: mix(palette.secondary, [180, 180, 190], 0.6),
        background: [6, 6, 10],
      };
    case 'aggressive':
      return {
        ...palette,
        primary: mix(palette.primary, [255, 40, 40], 0.35),
        accent: mix(palette.accent, [255, 255, 255], 0.2),
        glow: mix(palette.glow, [255, 0, 80], 0.4),
      };
    case 'plasma':
      return {
        ...palette,
        primary: mix(palette.primary, [120, 60, 255], 0.3),
        secondary: mix(palette.secondary, [40, 255, 210], 0.35),
        glow: mix(palette.glow, [255, 240, 90], 0.25),
      };
    case 'vortex':
      return {
        ...palette,
        background: [3, 0, 18],
        primary: mix(palette.primary, [95, 70, 255], 0.42),
        glow: mix(palette.glow, [180, 255, 60], 0.32),
      };
    case 'strobe-burst':
      return {
        ...palette,
        primary: mix(palette.primary, [255, 255, 255], 0.18),
        accent: mix(palette.accent, [255, 220, 40], 0.28),
        glow: mix(palette.glow, [0, 220, 255], 0.36),
      };
    case 'flash':
      return {
        ...palette,
        background: [0, 0, 0],
        primary: mix(palette.primary, [255, 255, 255], 0.35),
        glow: mix(palette.glow, [255, 255, 255], 0.5),
      };
    case 'neon-journey':
      return {
        ...palette,
        background: [5, 0, 20],
        primary: mix(palette.primary, [255, 0, 190], 0.32),
        secondary: mix(palette.secondary, [0, 220, 255], 0.38),
      };
    case 'cyberpunk':
      return {
        ...palette,
        primary: mix(palette.primary, [0, 255, 200], 0.45),
        secondary: mix(palette.secondary, [255, 0, 180], 0.35),
        background: [4, 0, 16],
      };
    case 'space':
      return {
        ...palette,
        primary: mix(palette.primary, [120, 160, 255], 0.4),
        background: [2, 4, 18],
        glow: mix(palette.glow, [180, 220, 255], 0.5),
      };
    case 'emotional':
      return {
        ...palette,
        primary: mix(palette.primary, [255, 120, 180], 0.3),
        background: [12, 6, 14],
      };
    case 'horizon-journey':
      return {
        ...palette,
        primary: mix(palette.primary, [80, 170, 210], 0.3),
        secondary: mix(palette.secondary, [255, 190, 90], 0.24),
        background: [6, 18, 32],
      };
    case 'cinematic':
      return {
        ...palette,
        background: [4, 4, 8],
        primary: mix(palette.primary, [255, 210, 140], 0.15),
      };
    case 'psychedelic':
      return {
        ...palette,
        accent: mix(palette.accent, [255, 0, 255], 0.35),
        glow: mix(palette.glow, [0, 255, 255], 0.35),
      };
    default:
      return palette;
  }
}
